import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import api from '../../services/api';
import './CompanyProfile.css';

const CompanyProfile = () => {
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [hasProfile, setHasProfile] = useState(false);
  const [company, setCompany] = useState({
    name: '',
    description: '',
    industry: {
      _id: "",
      name: ""
    },
    companySize: '',
    website: '',
    email: '',
    phone: '',
    address: {
      street: '',
      city: '',
      state: '',
      country: '',
      zipCode: ''
    },
    socialLinks: {
      linkedin: '',
      twitter: '',
      facebook: ''
    },
    logo: ''
  });
  const [indestriesList, setIndustriesList] = useState([]);

  useEffect(() => {
    fetchCompanyProfile();
    fetchIndustriesList();
  }, []);

  const fetchIndustriesList = async () => {
    try {
      const response = await api.get('/master/industry-types');
      if (response.data) {
        setIndustriesList(response.data);
      }
    } catch (error) {
      toast.error('Failed to fetch industries list');
    }
  }

  const fetchCompanyProfile = async () => {
    try {
      setLoading(true);
      const response = await api.get('/employer/company/profile');
      if (response.data && response.data._id) {
        setCompany(response.data);
        setHasProfile(true);
      } else {
        setHasProfile(false);
      }
      setLoading(false);
    } catch (error) {
      console.error('Error fetching company profile:', error);
      if (error.response?.status === 404 || error.code === 'PROFILE_INCOMPLETE') {
        setHasProfile(false);
      }
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (hasProfile) {
        await api.put('/employer/company/profile', company);
        toast.success('Company profile updated successfully');
      } else {
        await api.post('/employer/company/profile', company);
        toast.success('Company profile created successfully');
        setHasProfile(true);
      }
      setEditing(false);
      fetchCompanyProfile();
    } catch (error) {
      console.error('Error saving profile:', error);
      toast.error('Failed to save company profile');
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setCompany({
        ...company,
        [parent]: {
          ...company[parent],
          [child]: value
        }
      });
    } else {
      setCompany({ ...company, [name]: value });
    }
  };

  if (loading) {
    return (
      <div className="emp-cmp-profile cp-container">
        <div className="cp-loading"><div className="cp-spinner"></div><p>Loading profile…</p></div>
      </div>
    );
  }

  // Empty state — no profile yet
  if (!hasProfile && !editing) {
    return (
      <div className="emp-cmp-profile cp-container">
        <div className="cp-header">
          <div>
            <h1 className="cp-title">Company Profile</h1>
            <p className="cp-subtitle">Set up your company to attract top talent</p>
          </div>
        </div>
        <div className="cp-create-state">
          <div className="cp-create-icon">
            <span className="material-symbols-outlined">business</span>
          </div>
          <h2>Create Your Company Profile</h2>
          <p>A complete profile helps candidates trust your brand and increases application rates</p>
          <button className="cp-create-btn" onClick={() => setEditing(true)}>
            <span className="material-symbols-outlined">add_circle</span>
            Create Company Profile
          </button>
          <div className="cp-steps">
            {[
              { icon: 'info', label: 'Basic Info' },
              { icon: 'contact_page', label: 'Contact' },
              { icon: 'location_on', label: 'Address' },
              { icon: 'share', label: 'Social Links' },
            ].map(s => (
              <span key={s.label} className="cp-step">
                <span className="material-symbols-outlined">{s.icon}</span>{s.label}
              </span>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="emp-cmp-profile cp-container">
      {/* Header */}
      <div className="cp-header">
        <div>
          <h1 className="cp-title">{editing ? (hasProfile ? 'Edit Profile' : 'Create Profile') : 'Company Profile'}</h1>
          <p className="cp-subtitle">{editing ? 'Update your company information' : 'Your public company presence'}</p>
        </div>
        {!editing && hasProfile && (
          <button className="cp-edit-btn" onClick={() => setEditing(true)}>
            <span className="material-symbols-outlined">edit</span>
            Edit Profile
          </button>
        )}
      </div>

      {!editing && hasProfile ? (
        /* ── VIEW MODE ── */
        <div className="cp-view">
          {/* Hero */}
          <div className="cp-hero">
            <div className="cp-hero-logo">
              {company.logo
                ? <img src={company.logo} alt={company.name} />
                : <><span className="material-symbols-outlined">business</span></>
              }
            </div>
            <div className="cp-hero-info">
              <h2 className="cp-hero-name">{company.name}</h2>
              {company.industry?.name && (
                <p className="cp-hero-industry">
                  <span className="material-symbols-outlined">category</span>
                  {company.industry.name}
                </p>
              )}
              <div className="cp-hero-meta">
                {company.companySize && (
                  <span className="cp-hero-meta-item">
                    <span className="material-symbols-outlined">groups</span>{company.companySize} employees
                  </span>
                )}
                {company.website && (
                  <span className="cp-hero-meta-item">
                    <span className="material-symbols-outlined">language</span>
                    <a href={company.website} target="_blank" rel="noopener noreferrer">Visit Website</a>
                  </span>
                )}
                {(company.address?.city || company.address?.country) && (
                  <span className="cp-hero-meta-item">
                    <span className="material-symbols-outlined">location_on</span>
                    {[company.address.city, company.address.country].filter(Boolean).join(', ')}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* About */}
          {company.description && (
            <div className="cp-section">
              <h3 className="cp-section-title">
                <span className="material-symbols-outlined">description</span>About the Company
              </h3>
              <p className="cp-about-text">{company.description}</p>
            </div>
          )}

          {/* Contact */}
          {(company.email || company.phone || company.website) && (
            <div className="cp-section">
              <h3 className="cp-section-title">
                <span className="material-symbols-outlined">contact_page</span>Contact Information
              </h3>
              <div className="cp-contact-grid">
                {company.email && (
                  <div className="cp-contact-item">
                    <div className="cp-contact-icon"><span className="material-symbols-outlined">email</span></div>
                    <div>
                      <span className="cp-contact-label">Email</span>
                      <a href={`mailto:${company.email}`} className="cp-contact-value">{company.email}</a>
                    </div>
                  </div>
                )}
                {company.phone && (
                  <div className="cp-contact-item">
                    <div className="cp-contact-icon"><span className="material-symbols-outlined">phone</span></div>
                    <div>
                      <span className="cp-contact-label">Phone</span>
                      <a href={`tel:${company.phone}`} className="cp-contact-value">{company.phone}</a>
                    </div>
                  </div>
                )}
                {company.website && (
                  <div className="cp-contact-item">
                    <div className="cp-contact-icon"><span className="material-symbols-outlined">language</span></div>
                    <div>
                      <span className="cp-contact-label">Website</span>
                      <a href={company.website} target="_blank" rel="noopener noreferrer" className="cp-contact-value">{company.website}</a>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Address */}
          {(company.address?.street || company.address?.city || company.address?.country) && (
            <div className="cp-section">
              <h3 className="cp-section-title">
                <span className="material-symbols-outlined">location_on</span>Office Location
              </h3>
              <div className="cp-contact-grid">
                <div className="cp-contact-item">
                  <div className="cp-contact-icon"><span className="material-symbols-outlined">home_work</span></div>
                  <div>
                    <span className="cp-contact-label">Address</span>
                    <span className="cp-contact-value">
                      {[company.address?.street, company.address?.city, company.address?.state, company.address?.country].filter(Boolean).join(', ')}
                      {company.address?.zipCode && ` – ${company.address.zipCode}`}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Social */}
          {(company.socialLinks?.linkedin || company.socialLinks?.twitter || company.socialLinks?.facebook) && (
            <div className="cp-section">
              <h3 className="cp-section-title">
                <span className="material-symbols-outlined">share</span>Social Media
              </h3>
              <div className="cp-social-grid">
                {company.socialLinks?.linkedin && (
                  <a href={company.socialLinks.linkedin} target="_blank" rel="noopener noreferrer" className="cp-social-btn">
                    <span className="material-symbols-outlined">link</span>LinkedIn
                  </a>
                )}
                {company.socialLinks?.twitter && (
                  <a href={company.socialLinks.twitter} target="_blank" rel="noopener noreferrer" className="cp-social-btn">
                    <span className="material-symbols-outlined">link</span>Twitter / X
                  </a>
                )}
                {company.socialLinks?.facebook && (
                  <a href={company.socialLinks.facebook} target="_blank" rel="noopener noreferrer" className="cp-social-btn">
                    <span className="material-symbols-outlined">link</span>Facebook
                  </a>
                )}
              </div>
            </div>
          )}
        </div>
      ) : (
        /* ── EDIT MODE ── */
        <form onSubmit={handleSubmit} className="cp-form">
          {/* Basic Info */}
          <div className="cp-form-section">
            <h3 className="cp-form-section-title">
              <span className="material-symbols-outlined">info</span>Basic Information
            </h3>
            <div className="cp-form-group">
              <label>Company Name *</label>
              <input type="text" name="name" value={company.name} onChange={handleChange} required placeholder="Acme Corp" />
            </div>
            <div className="cp-form-group" style={{ marginTop: '14px' }}>
              <label>Description *</label>
              <textarea name="description" value={company.description} onChange={handleChange} rows="4" required placeholder="Tell candidates what your company does, its mission and culture…" />
            </div>
            <div className="cp-form-row" style={{ marginTop: '14px' }}>
              <div className="cp-form-group">
                <label>Industry *</label>
                <select name="industry" value={company.industry._id} onChange={handleChange}>
                  <option value="">Select Industry</option>
                  {indestriesList.map(ind => <option key={ind._id} value={ind._id}>{ind.name}</option>)}
                </select>
              </div>
              <div className="cp-form-group">
                <label>Company Size</label>
                <select name="companySize" value={company.companySize} onChange={handleChange}>
                  <option value="">Select Size</option>
                  <option value="1-10">1–10 employees</option>
                  <option value="11-50">11–50 employees</option>
                  <option value="51-200">51–200 employees</option>
                  <option value="201-500">201–500 employees</option>
                  <option value="501-1000">501–1000 employees</option>
                  <option value="1000+">1000+ employees</option>
                </select>
              </div>
            </div>
          </div>

          {/* Contact */}
          <div className="cp-form-section">
            <h3 className="cp-form-section-title">
              <span className="material-symbols-outlined">contact_page</span>Contact Information
            </h3>
            <div className="cp-form-group">
              <label>Website</label>
              <input type="url" name="website" value={company.website} onChange={handleChange} placeholder="https://example.com" />
            </div>
            <div className="cp-form-row" style={{ marginTop: '14px' }}>
              <div className="cp-form-group">
                <label>Email</label>
                <input type="email" name="email" value={company.email} onChange={handleChange} placeholder="info@company.com" />
              </div>
              <div className="cp-form-group">
                <label>Phone</label>
                <input type="tel" name="phone" value={company.phone} onChange={handleChange} placeholder="+1 234 567 8900" />
              </div>
            </div>
          </div>

          {/* Address */}
          <div className="cp-form-section">
            <h3 className="cp-form-section-title">
              <span className="material-symbols-outlined">location_on</span>Office Address
            </h3>
            <div className="cp-form-group">
              <label>Street Address</label>
              <input type="text" name="address.street" value={company.address?.street || ''} onChange={handleChange} placeholder="123 Main St" />
            </div>
            <div className="cp-form-row" style={{ marginTop: '14px' }}>
              <div className="cp-form-group">
                <label>City</label>
                <input type="text" name="address.city" value={company.address?.city || ''} onChange={handleChange} />
              </div>
              <div className="cp-form-group">
                <label>State / Province</label>
                <input type="text" name="address.state" value={company.address?.state || ''} onChange={handleChange} />
              </div>
            </div>
            <div className="cp-form-row" style={{ marginTop: '14px' }}>
              <div className="cp-form-group">
                <label>Country</label>
                <input type="text" name="address.country" value={company.address?.country || ''} onChange={handleChange} />
              </div>
              <div className="cp-form-group">
                <label>ZIP / Postal Code</label>
                <input type="text" name="address.zipCode" value={company.address?.zipCode || ''} onChange={handleChange} />
              </div>
            </div>
          </div>

          {/* Social */}
          <div className="cp-form-section">
            <h3 className="cp-form-section-title">
              <span className="material-symbols-outlined">share</span>Social Media
            </h3>
            <div className="cp-form-group">
              <label>LinkedIn</label>
              <input type="url" name="socialLinks.linkedin" value={company.socialLinks?.linkedin || ''} onChange={handleChange} placeholder="https://linkedin.com/company/…" />
            </div>
            <div className="cp-form-row" style={{ marginTop: '14px' }}>
              <div className="cp-form-group">
                <label>Twitter / X</label>
                <input type="url" name="socialLinks.twitter" value={company.socialLinks?.twitter || ''} onChange={handleChange} placeholder="https://twitter.com/…" />
              </div>
              <div className="cp-form-group">
                <label>Facebook</label>
                <input type="url" name="socialLinks.facebook" value={company.socialLinks?.facebook || ''} onChange={handleChange} placeholder="https://facebook.com/…" />
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="cp-form-actions">
            {hasProfile && (
              <button type="button" className="cp-cancel-btn" onClick={() => setEditing(false)}>
                <span className="material-symbols-outlined">close</span>Cancel
              </button>
            )}
            <button type="submit" className="cp-save-btn">
              <span className="material-symbols-outlined">save</span>
              {hasProfile ? 'Save Changes' : 'Create Profile'}
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

export default CompanyProfile;
